import { RRule } from 'rrule'
import { Temporal } from 'temporal-polyfill'

/**
 * Convert RRule weekday (0=Monday, 6=Sunday) to JavaScript weekday (0=Sunday, 6=Saturday)
 * RRule:      0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
 * JavaScript: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
 */
const rruleWeekdayToJs = (rruleWeekday: number): number => {
  return (rruleWeekday + 1) % 7
}

interface ParsedNthWeekday {
  weekday: number
  setpos: number
}

const getFirstValue = <T>(value: T | T[] | null | undefined): T | undefined =>
  Array.isArray(value) ? value[0] : (value ?? undefined)

const getWeekdayNumber = (weekdayValue: unknown): number | undefined => {
  if (typeof weekdayValue === 'number') {
    return weekdayValue
  }

  if (
    weekdayValue &&
    typeof weekdayValue === 'object' &&
    'weekday' in weekdayValue &&
    typeof weekdayValue.weekday === 'number'
  ) {
    return weekdayValue.weekday
  }

  return undefined
}

const parseNthWeekday = (
  byweekday: unknown,
  bysetpos?: number | number[] | null,
): ParsedNthWeekday | undefined => {
  const weekdayValue = getFirstValue(
    Array.isArray(byweekday) ? byweekday : [byweekday],
  )
  const weekday = getWeekdayNumber(weekdayValue)
  const setpos =
    weekdayValue &&
    typeof weekdayValue === 'object' &&
    'n' in weekdayValue &&
    typeof weekdayValue.n === 'number'
      ? weekdayValue.n
      : getFirstValue(bysetpos)

  if (weekday === undefined || setpos === undefined) {
    return undefined
  }

  return {
    weekday: rruleWeekdayToJs(weekday),
    setpos,
  }
}

/**
 * Form state for repeat configuration
 */
export interface RepeatFormState {
  frequency: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  interval: number // e.g., "every 2 years" = interval: 2
  // For weekly patterns
  byweekday?: number[] // Array of weekdays: 0=Sunday, 1=Monday, ..., 6=Saturday
  // For monthly patterns
  monthlyType?: 'SPECIFIC_DATE' | 'NTH_WEEKDAY' // "17th" vs "3rd Wednesday"
  // For yearly patterns
  yearlyType: 'SPECIFIC_DATE' | 'NTH_WEEKDAY' // "4th of July" vs "1st Saturday of July"
  month?: number // 1-12
  day?: number // 1-31 for SPECIFIC_DATE (used by both monthly and yearly)
  weekday?: number // 0=Sunday, 1=Monday, etc. for NTH_WEEKDAY (used by both monthly and yearly)
  setpos?: number // 1=first, 2=second, -1=last, etc. for NTH_WEEKDAY (used by both monthly and yearly)
  // End condition
  endType: 'NEVER' | 'AFTER' | 'ON_DATE'
  count?: number // For AFTER
  until?: string // ISO date string for ON_DATE
}

/**
 * Parse an RRule string into form-friendly state
 */
export const parseRRuleToFormState = (
  rrule: string | null | undefined,
  dtstart: string,
): RepeatFormState => {
  // Default state (no repeat)
  const defaultState: RepeatFormState = {
    frequency: 'NONE',
    interval: 1,
    monthlyType: 'SPECIFIC_DATE',
    yearlyType: 'SPECIFIC_DATE',
    endType: 'NEVER',
  }

  if (!rrule) {
    return defaultState
  }

  try {
    const rule = RRule.fromString(rrule)
    const options = rule.origOptions

    // Determine frequency
    let frequency: RepeatFormState['frequency'] = 'NONE'
    if (options.freq === RRule.YEARLY) frequency = 'YEARLY'
    else if (options.freq === RRule.MONTHLY) frequency = 'MONTHLY'
    else if (options.freq === RRule.WEEKLY) frequency = 'WEEKLY'
    else if (options.freq === RRule.DAILY) frequency = 'DAILY'

    // Get interval (default to 1)
    const interval = options.interval || 1

    // Parse start date to extract month/day info
    const startDate = parseToDate(dtstart)
    let month = startDate.getMonth() + 1 // 1-12 (default from dtstart)
    const day = startDate.getDate() // 1-31
    const dayOfWeek = startDate.getDay() // 0=Sunday, 1=Monday, etc.

    // Initialize pattern type defaults
    let monthlyType: 'SPECIFIC_DATE' | 'NTH_WEEKDAY' = 'SPECIFIC_DATE'
    let yearlyType: 'SPECIFIC_DATE' | 'NTH_WEEKDAY' = 'SPECIFIC_DATE'
    let parsedDay: number | undefined = day
    let parsedWeekday: number | undefined
    let parsedSetpos: number | undefined
    let byweekday: number[] | undefined

    // Handle WEEKLY frequency - extract selected weekdays
    if (frequency === 'WEEKLY') {
      if (options.byweekday) {
        const weekdays = Array.isArray(options.byweekday)
          ? options.byweekday
          : [options.byweekday]
        // Convert from RRule weekday convention (0=Monday) to JS convention (0=Sunday)
        byweekday = weekdays.map((wd) => {
          if (typeof wd === 'number') return rruleWeekdayToJs(wd)
          if (wd && typeof wd === 'object') return rruleWeekdayToJs(wd.weekday)
          return 0
        })
      } else {
        // Default to the start date's day of week
        byweekday = [dayOfWeek]
      }
    }

    // Handle MONTHLY frequency
    if (frequency === 'MONTHLY') {
      const nthWeekday = parseNthWeekday(options.byweekday, options.bysetpos)

      if (nthWeekday) {
        // Nth weekday pattern (e.g., "3rd Wednesday")
        monthlyType = 'NTH_WEEKDAY'
        parsedSetpos = nthWeekday.setpos
        parsedWeekday = nthWeekday.weekday
        parsedDay = undefined
      } else if (options.bymonthday) {
        // Specific day pattern (e.g., "17th")
        monthlyType = 'SPECIFIC_DATE'
        parsedDay = Array.isArray(options.bymonthday)
          ? options.bymonthday[0]
          : options.bymonthday
      } else {
        // Default to specific day based on dtstart
        monthlyType = 'SPECIFIC_DATE'
        parsedDay = day
        // Also set weekday/setpos defaults for switching to NTH_WEEKDAY
        parsedWeekday = dayOfWeek
        parsedSetpos = Math.ceil(day / 7)
      }
    }

    // Handle YEARLY frequency
    if (frequency === 'YEARLY') {
      // Use bymonth from RRule if available, otherwise use month from dtstart
      if (options.bymonth) {
        month = Array.isArray(options.bymonth)
          ? options.bymonth[0]
          : options.bymonth
      }

      // Check if using an nth weekday pattern. RRule parses `BYDAY=-1MO`
      // into a Weekday object with `n`, not `bysetpos`.
      const nthWeekday = parseNthWeekday(options.byweekday, options.bysetpos)

      if (nthWeekday) {
        yearlyType = 'NTH_WEEKDAY'
        parsedSetpos = nthWeekday.setpos
        parsedWeekday = nthWeekday.weekday
        parsedDay = undefined
      } else if (options.bymonthday) {
        // Specific date pattern (e.g., "4th of July")
        yearlyType = 'SPECIFIC_DATE'
        parsedDay = Array.isArray(options.bymonthday)
          ? options.bymonthday[0]
          : options.bymonthday
        parsedWeekday = undefined
        parsedSetpos = undefined
      } else {
        // Default to specific date based on dtstart
        yearlyType = 'SPECIFIC_DATE'
        parsedDay = day
      }
    }

    // Determine end condition
    let endType: 'NEVER' | 'AFTER' | 'ON_DATE' = 'NEVER'
    let count: number | undefined
    let until: string | undefined

    if (options.count) {
      endType = 'AFTER'
      count = options.count
    } else if (options.until) {
      endType = 'ON_DATE'
      // Convert Date to ISO string
      until = options.until.toISOString().split('T')[0] // YYYY-MM-DD
    }

    return {
      frequency,
      interval,
      byweekday: frequency === 'WEEKLY' ? byweekday : undefined,
      monthlyType: frequency === 'MONTHLY' ? monthlyType : undefined,
      yearlyType,
      month: frequency === 'YEARLY' ? month : undefined,
      day: parsedDay,
      weekday: parsedWeekday,
      setpos: parsedSetpos,
      endType,
      count,
      until,
    }
  } catch (error) {
    console.error('Error parsing RRule:', error)
    return defaultState
  }
}

/**
 * Convert JavaScript weekday (0=Sunday, 6=Saturday) to RRule weekday (0=Monday, 6=Sunday)
 * JavaScript: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
 * RRule:      0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
 */
const jsWeekdayToRRule = (jsWeekday: number): number => {
  return jsWeekday === 0 ? 6 : jsWeekday - 1
}

/**
 * Convert form state back to RRule string
 */
export const createRRuleFromFormState = (
  formState: RepeatFormState,
  _dtstart: string,
): string | null => {
  if (formState.frequency === 'NONE') {
    return null
  }

  try {
    // Note: dtstart is NOT included in the RRule options to match legacy behavior.
    // The dtstart is sent separately to the API, not embedded in the rrule string.
    const options: Partial<ConstructorParameters<typeof RRule>[0]> = {
      interval: formState.interval || 1,
    }

    // Set frequency
    switch (formState.frequency) {
      case 'DAILY':
        options.freq = RRule.DAILY
        break
      case 'WEEKLY':
        options.freq = RRule.WEEKLY
        // Handle weekly patterns - selected weekdays
        // Convert from JS weekday convention (0=Sunday) to RRule convention (0=Monday)
        if (formState.byweekday && formState.byweekday.length > 0) {
          options.byweekday = formState.byweekday.map(jsWeekdayToRRule)
        }
        break
      case 'MONTHLY':
        options.freq = RRule.MONTHLY
        // Handle monthly patterns
        if (formState.monthlyType === 'SPECIFIC_DATE') {
          // "17th" pattern - repeat on specific day of month
          if (formState.day) {
            options.bymonthday = [formState.day]
          }
        } else if (formState.monthlyType === 'NTH_WEEKDAY') {
          // "3rd Wednesday" pattern - repeat on nth weekday of month
          if (formState.setpos !== undefined) {
            options.bysetpos = [formState.setpos]
          }
          if (formState.weekday !== undefined) {
            // Convert from JS weekday convention (0=Sunday) to RRule convention (0=Monday)
            options.byweekday = [jsWeekdayToRRule(formState.weekday)]
          }
        }
        break
      case 'YEARLY':
        options.freq = RRule.YEARLY
        // Handle yearly patterns
        if (formState.yearlyType === 'SPECIFIC_DATE') {
          // "4th of July" pattern
          if (formState.month) {
            options.bymonth = [formState.month]
          }
          if (formState.day) {
            options.bymonthday = [formState.day]
          }
        } else if (formState.yearlyType === 'NTH_WEEKDAY') {
          // "1st Saturday of July" pattern
          if (formState.month) {
            options.bymonth = [formState.month]
          }
          if (formState.setpos !== undefined) {
            options.bysetpos = [formState.setpos]
          }
          if (formState.weekday !== undefined) {
            // Convert from JS weekday convention (0=Sunday) to RRule convention (0=Monday)
            options.byweekday = [jsWeekdayToRRule(formState.weekday)]
          }
        }
        break
    }

    // Set end condition
    if (formState.endType === 'AFTER' && formState.count) {
      options.count = formState.count
    } else if (formState.endType === 'ON_DATE' && formState.until) {
      // Parse until date and set to end of day in start date's timezone
      const untilDate = new Date(formState.until)
      untilDate.setHours(23, 59, 59, 999)
      options.until = untilDate
    }

    const rule = new RRule(options)
    // Strip 'RRULE:' prefix to match legacy format
    return rule.toString().replace('RRULE:', '')
  } catch (error) {
    console.error('Error creating RRule:', error)
    return null
  }
}

/**
 * Parse a date string (various formats) to JavaScript Date
 */
const parseToDate = (dateString: string): Date => {
  // Handle compact format (YYYYMMDD or YYYYMMDDTHHMMSS)
  if (dateString.length === 8) {
    // YYYYMMDD
    const year = parseInt(dateString.slice(0, 4), 10)
    const month = parseInt(dateString.slice(4, 6), 10) - 1
    const day = parseInt(dateString.slice(6, 8), 10)
    return new Date(year, month, day)
  } else if (dateString.length >= 15 && !dateString.includes('-')) {
    // YYYYMMDDTHHMMSS
    const year = parseInt(dateString.slice(0, 4), 10)
    const month = parseInt(dateString.slice(4, 6), 10) - 1
    const day = parseInt(dateString.slice(6, 8), 10)
    const hour = parseInt(dateString.slice(9, 11), 10)
    const minute = parseInt(dateString.slice(11, 13), 10)
    const second = parseInt(dateString.slice(13, 15), 10) || 0
    return new Date(year, month, day, hour, minute, second)
  }

  // Try ISO format
  return new Date(dateString)
}

/**
 * Format a ZonedDateTime to YYYYMMDDTHHMMSS format for API
 */
export const formatDateTimeForApi = (zdt: Temporal.ZonedDateTime): string => {
  const year = zdt.year.toString().padStart(4, '0')
  const month = zdt.month.toString().padStart(2, '0')
  const day = zdt.day.toString().padStart(2, '0')
  const hour = zdt.hour.toString().padStart(2, '0')
  const minute = zdt.minute.toString().padStart(2, '0')
  const second = zdt.second.toString().padStart(2, '0')
  return `${year}${month}${day}T${hour}${minute}${second}`
}

/**
 * Format a ZonedDateTime to YYYY-MM-DD format for date inputs
 */
export const formatDateForInput = (zdt: Temporal.ZonedDateTime): string => {
  const year = zdt.year.toString().padStart(4, '0')
  const month = zdt.month.toString().padStart(2, '0')
  const day = zdt.day.toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a ZonedDateTime to h:mm AM/PM format for time inputs
 */
export const formatTimeForInput = (zdt: Temporal.ZonedDateTime): string => {
  const meridiem = zdt.hour >= 12 ? 'PM' : 'AM'
  const hour = (zdt.hour % 12 || 12).toString()
  const minute = zdt.minute.toString().padStart(2, '0')
  return `${hour}:${minute} ${meridiem}`
}

/**
 * Parse a date string (YYYY-MM-DD) and time string to ZonedDateTime
 */
export const parseDateTimeFromInput = (
  dateString: string,
  timeString: string,
  timeZone: string,
): Temporal.ZonedDateTime => {
  const normalizedDate = dateString.trim()
  if (!normalizedDate) {
    throw new Error('Please enter a valid date')
  }

  let year: number
  let month: number
  let day: number

  // YYYY-MM-DD
  const isoMatch = normalizedDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (isoMatch) {
    year = Number(isoMatch[1])
    month = Number(isoMatch[2])
    day = Number(isoMatch[3])
  } else {
    // MM/DD/YYYY
    const usMatch = normalizedDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (!usMatch) {
      throw new Error('Please enter a valid date')
    }
    month = Number(usMatch[1])
    day = Number(usMatch[2])
    year = Number(usMatch[3])
  }

  // Validate that the date really exists (e.g. rejects 02/29 on non-leap years)
  try {
    Temporal.PlainDate.from({ year, month, day })
  } catch {
    throw new Error('Please enter a valid date')
  }

  const normalizedTime = timeString.trim()
  if (!normalizedTime) {
    throw new Error('Please enter a valid time')
  }

  // Supports:
  // - HH:MM (24h)
  // - H:MM AM/PM
  // - H:MMAM / H:MMPM
  const timeMatch = normalizedTime.match(
    /^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/,
  )

  if (!timeMatch) {
    throw new Error('Please enter a valid time')
  }

  let hour = Number(timeMatch[1])
  const minute = Number(timeMatch[2])
  const meridiem = timeMatch[3]?.toUpperCase()

  if (minute < 0 || minute > 59) {
    throw new Error('Please enter a valid time')
  }

  if (meridiem) {
    if (hour < 1 || hour > 12) {
      throw new Error('Please enter a valid time')
    }
    hour = meridiem === 'PM' ? (hour % 12) + 12 : hour % 12
  } else if (hour < 0 || hour > 23) {
    throw new Error('Please enter a valid time')
  }

  return Temporal.ZonedDateTime.from({
    year,
    month,
    day,
    hour,
    minute,
    second: 0,
    timeZone,
  })
}

/**
 * Get weekday name from number (0=Sunday, 1=Monday, etc.)
 */
export const getWeekdayName = (weekday: number): string => {
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  return weekdays[weekday] || 'Sunday'
}

/**
 * Get month name from number (1-12)
 */
export const getMonthName = (month: number): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  return months[month - 1] || 'January'
}

/**
 * Get ordinal suffix for day (1st, 2nd, 3rd, 4th, etc.)
 */
export const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th'
  }
  const lastDigit = day % 10
  switch (lastDigit) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

/**
 * Format setpos for display (1st, 2nd, -1=last, etc.)
 */
export const formatSetpos = (setpos: number): string => {
  if (setpos < 0) {
    return 'last'
  }
  return `${setpos}${getOrdinalSuffix(setpos)}`
}
